import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import QRCode from "qrcode";
import { toast } from "sonner";

import { apiError, parkingApi, useCurrentShift, usePkList, usePkMutation, usePkSummary, type PaymentMethod, type PkPayment, type PkTicket, type Quote } from "../api/parkingApi";
import { ConsolePage, DetailRow, KpiTile, Panel, StatusChip, ToolButton, formatDateTime, relativeTime, toneOf, useTicker } from "../components/console/Console";
import FormModal, { type FormValues } from "../components/console/FormModal";
import { FeeBreakdown } from "./TicketsScreen";
import { METHOD_ICON, PlateBadge, TICKET_TONE, duration, errorText, money } from "./parkingUi";
import { buildTicketPdf, openPdf } from "./parkingPdf";

// ===========================================================================
// Control de estacionamientos → Caja de cobro rápida: escanear o teclear folio,
// QR o placa → el SERVIDOR cotiza (tarifa, tolerancia, tope, nocturna,
// validación, boleto perdido) → cobrar en efectivo (con cambio), terminal
// bancaria, Stripe o liga de pago → recibo PDF con QR de autofactura.
// Todo cobro en caja queda dentro del corte abierto del cajero.
// ===========================================================================

const METHODS: PaymentMethod[] = ["cash", "card_terminal", "stripe", "link"];
const BILLS = [20, 50, 100, 200, 500, 1000];

export default function CashierScreen() {
  const { t, i18n } = useTranslation("parking");
  const [params] = useSearchParams();
  const now = useTicker(30_000);
  const inputRef = useRef<HTMLInputElement>(null);
  const [code, setCode] = useState("");
  const [validation, setValidation] = useState("");
  const [lost, setLost] = useState(false);
  const [quote, setQuote] = useState<Quote | null>(null);
  const [quoting, setQuoting] = useState(false);
  const [method, setMethod] = useState<PaymentMethod>("cash");
  const [received, setReceived] = useState("");
  const [reference, setReference] = useState("");
  const [done, setDone] = useState<{ payment: PkPayment; ticket: PkTicket | null; url?: string | null; qr?: string } | null>(null);
  const [openShiftModal, setOpenShiftModal] = useState(false);

  const shiftQ = useCurrentShift();
  const shift = shiftQ.data ?? null;
  const lotsQ = usePkList("lots");
  const paymentsQ = usePkList("payments", shift ? { shift_id: shift.id, limit: 200 } : undefined, { enabled: Boolean(shift), refetchInterval: 30_000 });

  const pay = usePkMutation(parkingApi.pay);
  const exit = usePkMutation((id: string) => parkingApi.exit({ ticket_id: id }));
  const openShift = usePkMutation(parkingApi.openShift);

  const total = quote?.amount.total ?? 0;
  const cash = Number(received || 0);
  const change = Math.max(0, Math.round((cash - total) * 100) / 100);

  const doQuote = async (body: { code?: string; ticket_id?: string }, opts: { validation_code?: string; lost?: boolean } = {}) => {
    setQuoting(true);
    setDone(null);
    try {
      const q = await parkingApi.quote({ ...body, validation_code: opts.validation_code || undefined, lost: opts.lost || undefined });
      setQuote(q);
      setReceived("");
      if (q.amount.total === 0) setMethod("cash");
    } catch (err) {
      setQuote(null);
      toast.error(errorText(err, t("cashier.notFound")));
    } finally {
      setQuoting(false);
    }
  };

  // Llegó desde el mapa, la lista o el tablero con un ticket.
  useEffect(() => {
    const id = params.get("ticket");
    if (id) void doQuote({ ticket_id: id });
    else inputRef.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const submitCode = (e: FormEvent) => {
    e.preventDefault();
    const c = code.trim();
    if (c) void doQuote({ code: c }, { validation_code: validation.trim(), lost });
  };

  const requote = (opts: { validation_code?: string; lost?: boolean }) => {
    if (quote) void doQuote({ ticket_id: quote.ticket.id }, { validation_code: opts.validation_code ?? validation.trim(), lost: opts.lost ?? lost });
  };

  const summaryQ = usePkSummary(undefined, { refetchInterval: 60_000 });
  const refunds = summaryQ.data?.refunds ?? [];

  const charge = async (replacePending = false) => {
    if (!quote) return;
    try {
      const out = await pay.mutateAsync({
        replace_pending: replacePending || undefined,
        ticket_id: quote.ticket.id,
        method,
        received: method === "cash" ? cash : undefined,
        reference: method === "card_terminal" ? reference.trim() || undefined : undefined,
        validation_code: validation.trim() || undefined,
        lost: lost || undefined,
        success_url: method === "stripe" ? window.location.href : undefined,
        cancel_url: method === "stripe" ? window.location.href : undefined,
      });
      const qr = out.url ? await QRCode.toDataURL(out.url, { margin: 1, width: 240 }) : undefined;
      setDone({ payment: out.payment, ticket: out.ticket, url: out.url, qr });
      if (out.payment.status === "paid") {
        toast.success(t("cashier.paid", { interpolation: { escapeValue: false }, amount: money(out.payment.amount, i18n.language) }));
        if (out.ticket) openPdf(await buildTicketPdf({ ticket: out.ticket, payment: out.payment, t, lang: i18n.language }), `${out.ticket.folio}-recibo.pdf`);
      } else {
        toast.success(t("cashier.linkReady"));
      }
      setQuote(null);
      setCode("");
      setValidation("");
      setLost(false);
      setReference("");
      inputRef.current?.focus();
    } catch (err) {
      // Hay un cobro en línea vivo: sólo se reemplaza si el cajero lo confirma.
      if (apiError(err).code === "parkingPaymentPending" && !replacePending && window.confirm(t("cashier.replacePending"))) return charge(true);
      toast.error(errorText(err, t("common.saveError")));
    }
  };

  const freeExit = async () => {
    if (!quote) return;
    try {
      await exit.mutateAsync(quote.ticket.id);
      toast.success(t("tickets.exited"));
      setQuote(null);
      setCode("");
      inputRef.current?.focus();
    } catch (err) {
      toast.error(errorText(err, t("common.saveError")));
    }
  };

  const submitShift = async (v: FormValues) => {
    try {
      await openShift.mutateAsync({ lot_id: String(v.lot_id), opening_float: Number(v.opening_float || 0) });
      toast.success(t("shifts.opened"));
      setOpenShiftModal(false);
    } catch (err) {
      toast.error(errorText(err, t("common.saveError")));
    }
  };

  const needsShift = !shift && (method === "cash" || method === "card_terminal");
  const canCharge = Boolean(quote) && !pay.isPending && !needsShift && (method !== "cash" || cash >= total) && !(quote?.ticket.status === "paid" && !quote.overstay);
  const shiftPayments = useMemo(() => (paymentsQ.data ?? []).slice(0, 12), [paymentsQ.data]);

  return (
    <ConsolePage
      icon="ri-cash-line"
      title={t("cashier.title")}
      subtitle={t("cashier.subtitle")}
      actions={
        shift ? (
          <span className="flex items-center gap-2 rounded-lg border border-line bg-card px-3 py-1.5 text-xs text-content-secondary">
            <span className="h-2 w-2 animate-pulse rounded-full bg-success" />
            {t("cashier.shiftOpen", { interpolation: { escapeValue: false }, number: shift.number, lot: shift.lot_name })} · {relativeTime(shift.opened_at, i18n.language, now)}
          </span>
        ) : (
          <ToolButton icon="ri-door-open-line" variant="primary" onClick={() => setOpenShiftModal(true)}>{t("shifts.open")}</ToolButton>
        )
      }
    >
      {refunds.length > 0 && (
        <div role="alert" className="flex items-center gap-2 rounded-xl border border-error/40 bg-error/10 px-3 py-2 text-sm text-content">
          <i className="ri-refund-2-line text-error" /> {t("cashier.refundsAlert", { interpolation: { escapeValue: false }, count: refunds.length })}
        </div>
      )}
      {!shift && !shiftQ.isLoading && (
        <div className="flex items-center gap-2 rounded-xl border border-warning/40 bg-warning/10 px-3 py-2 text-sm text-content">
          <i className="ri-error-warning-line text-warning" /> {t("cashier.noShift")}
        </div>
      )}
      <div className="grid gap-4 xl:grid-cols-3">
        <div className="space-y-4 xl:col-span-2">
          <Panel title={t("cashier.scan")} icon="ri-qr-scan-2-line">
            <form onSubmit={submitCode} className="flex flex-col gap-2 sm:flex-row">
              <label className="flex flex-1 items-center gap-2 rounded-lg border-2 border-primary-500/50 bg-app px-3 py-2 focus-within:border-primary-500">
                <i className="ri-barcode-line text-xl text-primary-500" />
                <input
                  ref={inputRef}
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder={t("cashier.scanPlaceholder")}
                  aria-label={t("cashier.scanPlaceholder")}
                  autoComplete="off"
                  className="w-full bg-transparent font-mono text-lg text-content outline-none placeholder:font-sans placeholder:text-sm placeholder:text-content-faint"
                />
              </label>
              <button type="submit" disabled={quoting || !code.trim()} className="rounded-lg bg-primary-500 px-5 py-2 text-sm font-semibold text-on-primary hover:bg-primary-400 disabled:opacity-50">
                {quoting ? t("common.loading") : t("cashier.quote")}
              </button>
            </form>
            <div className="mt-2 flex flex-wrap items-center gap-3 text-sm">
              <label className="flex items-center gap-2 text-content-secondary">
                <i className="ri-coupon-3-line" />
                <input
                  value={validation}
                  onChange={(e) => setValidation(e.target.value.toUpperCase())}
                  onBlur={() => quote && requote({ validation_code: validation.trim() })}
                  placeholder={t("cashier.validationPlaceholder")}
                  aria-label={t("cashier.validationPlaceholder")}
                  className="w-44 rounded-md border border-line bg-app px-2 py-1 font-mono text-xs uppercase text-content outline-none focus:border-primary-500"
                />
              </label>
              <label className="flex items-center gap-2 text-content-secondary">
                <input
                  type="checkbox"
                  checked={lost}
                  onChange={(e) => {
                    setLost(e.target.checked);
                    if (quote) requote({ lost: e.target.checked });
                  }}
                  className="h-4 w-4 accent-primary-500"
                />
                {t("cashier.lostTicket")}
              </label>
            </div>
          </Panel>

          {quote && (
            <Panel title={`${t("common.ticket")} ${quote.ticket.folio}`} icon="ri-ticket-2-line" actions={<StatusChip tone={toneOf(TICKET_TONE, quote.ticket.status)}>{t(`ticketStatus.${quote.ticket.status}`)}</StatusChip>}>
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  {quote.ticket.entry_photo_url && <img src={quote.ticket.entry_photo_url} alt={t("tickets.entryPhoto")} className="mb-3 aspect-video w-full rounded-lg object-cover" />}
                  <DetailRow label={t("common.plate")}><PlateBadge plate={quote.ticket.plate} size="lg" /></DetailRow>
                  <DetailRow label={t("common.lot")}>{quote.lot.name}</DetailRow>
                  <DetailRow label={t("tickets.entry")}>{formatDateTime(quote.ticket.entry_at, i18n.language)}</DetailRow>
                  <DetailRow label={t("tickets.stay")}>{duration(quote.amount.minutes || Math.round((Date.parse(quote.quoted_at) - Date.parse(quote.ticket.entry_at)) / 60000))}</DetailRow>
                  <DetailRow label={t("tickets.rate")}>{quote.rate.name}</DetailRow>
                  {quote.validation && <DetailRow label={t("tickets.validation")}>{quote.validation.merchant_name}</DetailRow>}
                  {quote.overstay && <p className="mt-2 rounded-lg bg-warning/10 p-2 text-xs text-warning">{t("cashier.overstay")}</p>}
                </div>
                <div className="flex flex-col">
                  <div className="rounded-xl border border-line bg-app p-4 text-center">
                    <p className="text-xs uppercase tracking-wide text-content-muted">{t("cashier.toPay")}</p>
                    <p className="text-4xl font-bold tabular-nums text-content">{money(total, i18n.language)}</p>
                    {quote.amount.in_grace && <p className="mt-1 text-xs text-success">{t("cashier.inGrace")}</p>}
                  </div>
                  <div className="mt-3"><FeeBreakdown ticket={{ ...quote.ticket, amount: quote.amount }} /></div>

                  {total > 0 ? (
                    <div className="mt-4 space-y-3">
                      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                        {METHODS.map((m) => (
                          <button
                            key={m}
                            type="button"
                            aria-pressed={method === m}
                            onClick={() => setMethod(m)}
                            className={`flex flex-col items-center gap-1 rounded-lg border px-2 py-2 text-xs ${method === m ? "border-primary-500 bg-primary-500/10 text-primary-500" : "border-line text-content-secondary hover:bg-subtle"}`}
                          >
                            <i className={`${METHOD_ICON[m]} text-lg`} />
                            {t(`method.${m}`)}
                          </button>
                        ))}
                      </div>
                      {method === "cash" && (
                        <div>
                          <label className="block text-sm text-content-secondary">
                            {t("cashier.received")}
                            <input type="number" min={0} step="0.5" value={received} onChange={(e) => setReceived(e.target.value)} className="mt-1 w-full rounded-lg border border-line bg-app px-3 py-2 text-lg tabular-nums text-content outline-none focus:border-primary-500" />
                          </label>
                          <div className="mt-2 flex flex-wrap gap-1.5">
                            <button type="button" onClick={() => setReceived(String(total))} className="rounded-md border border-line px-2 py-1 text-xs text-content hover:bg-subtle">{t("cashier.exact")}</button>
                            {BILLS.filter((b) => b >= total).slice(0, 4).map((b) => (
                              <button key={b} type="button" onClick={() => setReceived(String(b))} className="rounded-md border border-line px-2 py-1 text-xs text-content hover:bg-subtle">${b}</button>
                            ))}
                          </div>
                          <p className="mt-2 flex justify-between text-sm"><span className="text-content-secondary">{t("cashier.change")}</span><span className="text-lg font-bold tabular-nums text-success">{money(change, i18n.language)}</span></p>
                        </div>
                      )}
                      {method === "card_terminal" && (
                        <label className="block text-sm text-content-secondary">
                          {t("cashier.reference")}
                          <input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="AUT-000000" className="mt-1 w-full rounded-lg border border-line bg-app px-3 py-2 text-content outline-none focus:border-primary-500" />
                        </label>
                      )}
                      {(method === "stripe" || method === "link") && <p className="rounded-lg bg-info/10 p-2 text-xs text-content-secondary">{t(`cashier.${method}Hint`)}</p>}
                      <button type="button" disabled={!canCharge} onClick={() => charge()} className="w-full rounded-lg bg-success px-4 py-3 text-base font-bold text-white hover:opacity-90 disabled:opacity-50">
                        <i className="ri-check-line" /> {pay.isPending ? t("common.saving") : t("cashier.chargeN", { interpolation: { escapeValue: false }, amount: money(total, i18n.language) })}
                      </button>
                      {needsShift && <p className="text-xs text-warning">{t("cashier.noShift")}</p>}
                    </div>
                  ) : (
                    <button type="button" onClick={freeExit} disabled={exit.isPending} className="mt-4 w-full rounded-lg bg-success px-4 py-3 text-base font-bold text-white hover:opacity-90 disabled:opacity-50">
                      <i className="ri-logout-box-r-line" /> {t("cashier.freeExit")}
                    </button>
                  )}
                </div>
              </div>
            </Panel>
          )}

          {done && (
            <Panel title={t("cashier.lastCharge")} icon="ri-receipt-line">
              <div className="flex flex-col items-center gap-4 sm:flex-row">
                {done.qr && <img src={done.qr} alt="QR" className="h-40 w-40 rounded-lg border border-line bg-white p-1" />}
                <div className="min-w-0 flex-1 text-sm">
                  <p className="text-lg font-bold text-content">{money(done.payment.amount, i18n.language)} · {t(`method.${done.payment.method}`)}</p>
                  <p className="text-content-secondary">{done.payment.folio} · {t(`paymentStatus.${done.payment.status}`)}</p>
                  {done.payment.method === "cash" && <p className="text-content-secondary">{t("cashier.change")}: {money(done.payment.change, i18n.language)}</p>}
                  {done.url && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      <a href={done.url} target="_blank" rel="noopener noreferrer" className="rounded-md bg-primary-500 px-3 py-1.5 text-xs font-medium text-on-primary">{t("cashier.openLink")}</a>
                      <button type="button" className="rounded-md border border-line px-3 py-1.5 text-xs text-content hover:bg-subtle" onClick={() => navigator.clipboard?.writeText(done.url ?? "").then(() => toast.success(t("common.copied")))}>{t("common.copyLink")}</button>
                    </div>
                  )}
                  {done.ticket && done.payment.status === "paid" && (
                    <button type="button" className="mt-2 rounded-md border border-line px-3 py-1.5 text-xs text-content hover:bg-subtle" onClick={async () => openPdf(await buildTicketPdf({ ticket: done.ticket!, payment: done.payment, t, lang: i18n.language }), `${done.payment.folio}-recibo.pdf`)}>
                      <i className="ri-printer-line" /> {t("tickets.printReceipt")}
                    </button>
                  )}
                </div>
              </div>
            </Panel>
          )}
        </div>

        <div className="space-y-4">
          {shift && (
            <div className="grid grid-cols-2 gap-3">
              <KpiTile icon="ri-money-dollar-circle-line" tone="success" label={t("shifts.total")} value={money(shift.running_total, i18n.language)} />
              <KpiTile icon="ri-receipt-line" tone="info" label={t("shifts.payments")} value={shift.running_count ?? 0} />
            </div>
          )}
          <Panel title={t("cashier.shiftPayments")} icon="ri-list-check-2" bodyClassName="max-h-[520px] overflow-y-auto">
            {!shift ? (
              <p className="p-3 text-sm text-content-secondary">{t("cashier.noShift")}</p>
            ) : !shiftPayments.length ? (
              <p className="p-3 text-sm text-content-secondary">{t("cashier.noPayments")}</p>
            ) : (
              <ul className="divide-y divide-line">
                {shiftPayments.map((p) => (
                  <li key={p.id} className="flex items-center gap-2 px-3 py-2 text-sm">
                    <i className={`${METHOD_ICON[p.method]} text-content-muted`} />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-mono text-xs text-content">{p.folio ?? p.holder_name}</span>
                      <span className="block text-[11px] text-content-muted">{relativeTime(p.paid_at || p.createdAt, i18n.language, now)}</span>
                    </span>
                    <span className="tabular-nums text-content">{money(p.amount, i18n.language)}</span>
                  </li>
                ))}
              </ul>
            )}
          </Panel>
        </div>
      </div>

      {openShiftModal && (
        <FormModal
          title={t("shifts.open")}
          submitLabel={t("shifts.open")}
          busy={openShift.isPending}
          onClose={() => setOpenShiftModal(false)}
          onSubmit={submitShift}
          initial={{ lot_id: lotsQ.data?.[0]?.id ?? "", opening_float: 500 }}
          fields={[
            { key: "lot_id", label: t("common.lot"), type: "select", required: true, options: (lotsQ.data ?? []).map((l) => ({ value: l.id, label: l.name })) },
            { key: "opening_float", label: t("shifts.openingFloat"), type: "number", min: 0, required: true, hint: t("shifts.openingFloatHint") },
          ]}
        />
      )}
    </ConsolePage>
  );
}
